import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';
import { E as EmblaCarousel } from './embla-carousel.esm-CCUHK2kO.js';
import { a as addPrevNextButtonClickHandlers } from './arrow-buttons-CY1f9syu.js';
import { a as addScrollBarListener } from './scrollbar-DHAl7Frv.js';
import { F } from './fancybox-BA187ItQ.js';

const addThumbButtonClickHandlers = (emblaApiMain, emblaApiThumb) => {
  const slidesThumbs = emblaApiThumb.slideNodes();

  const scrollToIndex = slidesThumbs.map((_, index) => () => emblaApiMain.goTo(index));

  slidesThumbs.forEach((slideNode, index) => {
    slideNode.addEventListener('click', scrollToIndex[index], false);
  });
};

const addToggleThumbButtonsActive = (emblaApiMain, emblaApiThumb) => {
  const slidesThumbs = emblaApiThumb.slideNodes();

  const toggleThumbBtnsState = () => {
    emblaApiThumb.goTo(emblaApiMain.selectedSnap());
    const previous = emblaApiMain.previousSnap();
    const selected = emblaApiMain.selectedSnap();
    slidesThumbs[previous].classList.remove('embla-thumbs__slide--selected');
    slidesThumbs[selected].classList.add('embla-thumbs__slide--selected');
  };

  emblaApiMain.on('select', toggleThumbBtnsState);
  toggleThumbBtnsState();
};

class ProductMedia extends HTMLElement {
  #unsubscribeVariantChange;

  /** @type {import('embla-carousel').EmblaCarouselType} */
  #embla;
  /** @type {import('embla-carousel').EmblaCarouselType} */
  #thumbsEmbla;
  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  #prevBtn = null;
  #nextBtn = null;
  #scrollbar = null;
  #thumbs = null;

  connectedCallback() {
    this.#initEmbla();
    this.#initVideoObserver();
    this.#initFancybox();

    this.#unsubscribeVariantChange = PubSub.subscribe(
      'variant:change',
      ({ id, data: { html } }) => {
        if (id !== this.dataset.productId) return;
        this.#handleMediaChange(html);
      }
    );
  }

  disconnectedCallback() {
    this.#teardown();
    this.#unsubscribeVariantChange?.();
  }

  #teardown() {
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
    this.#embla?.destroy();
    this.#embla = null;
    this.#thumbsEmbla?.destroy();
    this.#thumbsEmbla = null;
    F.unbind(this);
  }

  #initEmbla() {
    const viewport = this.querySelector('.embla--media .embla__viewport');
    if (!viewport) return;

    this.#removeHiddenSlidesOnMobile();

    this.#prevBtn = this.querySelector('.embla-button--prev');
    this.#nextBtn = this.querySelector('.embla-button--next');
    this.#scrollbar = this.querySelector('.embla__scrollbar');
    this.#thumbs = this.querySelector('.embla-thumbs');

    this.#initThumbsEmbla();

    this.#embla = EmblaCarousel(viewport, {
      direction: document.dir || 'ltr',
      slidesToScroll: 1,
      active: true,
      align: 'start',
      skipSnaps: true,
      startSnap: 0,
      loop: true,
      breakpoints: {
        '(min-width:768px)': { active: false },
      },
    });

    this.#bindEmblaControls();
  }

  #initThumbsEmbla() {
    if (!this.#thumbs) return;

    this.#thumbsEmbla = EmblaCarousel(this.#thumbs.querySelector('.embla-thumbs__viewport'), {
      direction: document.dir || 'ltr',
      skipSnaps: true,
    });
  }

  #bindEmblaControls() {
    if (this.#thumbs) {
      addThumbButtonClickHandlers(this.#embla, this.#thumbsEmbla);
      addToggleThumbButtonsActive(this.#embla, this.#thumbsEmbla);
    }

    if (this.#scrollbar) {
      addScrollBarListener(this.#embla, this.#scrollbar);
    }

    if (this.#prevBtn && this.#nextBtn) {
      addPrevNextButtonClickHandlers(this.#embla, this.#prevBtn, this.#nextBtn);
    }
  }

  #initFancybox() {
    F.bind(this, '[data-fancybox]', {
      Carousel: {
        Panzoom: {
          touch: (panzoom, event) => event?.target.nodeName !== 'MODEL-VIEWER',
        },
      },
    });
  }

  #removeHiddenSlidesOnMobile() {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    this.querySelectorAll('[data-hide-slide="true"]').forEach((slide) => slide.remove());
  }

  #initVideoObserver() {
    const videoSlides = [...this.querySelectorAll('.embla__slide')].filter((slide) =>
      slide.querySelector('video-player')
    );
    if (!videoSlides.length) return;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const player = entry.target.querySelector('video-player');
          if (!player) continue;
          entry.isIntersecting ? player.play() : player.pause();
        }
      },
      { threshold: 0.5 }
    );

    videoSlides.forEach((slide) => this.#videoObserver.observe(slide));
  }

  /**
   * Handle media change.
   * @param {HTMLElement} html - The HTML to render.
   */
  #handleMediaChange(html) {
    const productMedia = html.querySelector(
      `product-media[data-product-id="${this.dataset.productId}"]`
    );
    if (!productMedia) return;

    this.#teardown();
    this.innerHTML = productMedia.innerHTML;
    this.#initEmbla();
    this.#initVideoObserver();
    this.#initFancybox();
  }
}

customElements.define('product-media', ProductMedia);
